import { Grid, makeStyles, Theme } from "@material-ui/core";
import _ from "lodash";
import React, { FunctionComponent, useContext, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  FeedbackType,
  GeneDataGroupSource,
  useGeneDataGroupSourcesQuery,
} from "../../../api/smartmelts-api/graphql/generated";
import { ChefHat } from "../../../assets/icons/chef-hat";
import { NutrientSourceBlock } from "../../../components/modal/components/nutrient-source-block";
import { ContentText } from "../../../components/text/content-text";
import { HeadingText } from "../../../components/text/heading-text";
import { MenuSubcategoryDictionary } from "../../../models/categories";
import { NutritionContext } from "../context/nutrition-context";
import { AnswerContext } from "./context/answer-context";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    flex: 1,
    width: "100%",
  },
  contentText: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  link: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  toleranceWrapper: {
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2),
    },
  },
  emphasized: {
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
  sources: {
    paddingTop: theme.spacing(4),
  },
  sourceContainer: {
    paddingTop: theme.spacing(4),
  },
  chefHat: {
    paddingRight: theme.spacing(2),
  },
  sourcesTitle: {
    paddingBottom: theme.spacing(5),
  },
  sourceImageContainer: {
    paddingLeft: theme.spacing(8),
    paddingRight: theme.spacing(8),
  },
}));

interface IGeneRiskInfoAnswerProps {}

export const GeneRiskInfoAnswer: FunctionComponent<IGeneRiskInfoAnswerProps> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { activeQuestion } = useContext(AnswerContext);
  const { activeSubcategory, scrollTop } = useContext(NutritionContext);
  const geneDataGroupType = useMemo(() => MenuSubcategoryDictionary[activeSubcategory].geneDataGroupTypes[0], [
    activeSubcategory,
  ]);
  const [sources, setSources] = useState<GeneDataGroupSource[]>([]);
  useGeneDataGroupSourcesQuery({
    variables: {
      type: geneDataGroupType,
    },
    onCompleted: (data) => {
      setSources(_.orderBy(data.geneDataGroupSourcesByType, (source) => source.sortOrder, "asc"));
      scrollTop();
    },
    onError: (_error) => {
      setSources([]);
    },
  });

  return (
    <Grid container className={classes.container}>
      <HeadingText>
        {t(`twin_communication.${activeQuestion.question}.${FeedbackType.GENE_RISK_INFO}.heading`)}
      </HeadingText>
      <Grid item className={classes.contentText}>
        <ContentText light>
          <Trans
            i18nKey={`twin_communication.${activeQuestion.question}.${FeedbackType.GENE_RISK_INFO}.content_1`}
            components={[<span className={classes.emphasized} />]}
          />
        </ContentText>
      </Grid>
      <Grid item className={classes.contentText}>
        <ContentText light>
          <Trans
            i18nKey={`twin_communication.${activeQuestion.question}.${FeedbackType.GENE_RISK_INFO}.content_2`}
            components={[<span className={classes.emphasized} />]}
          />
        </ContentText>
      </Grid>
      <Grid item className={classes.contentText}>
        <ContentText small>
          <Trans
            i18nKey={`twin_communication.${activeQuestion.question}.${FeedbackType.GENE_RISK_INFO}.subheading`}
            components={[<span className={classes.emphasized} />]}
          />
        </ContentText>
      </Grid>
      <Grid item className={classes.contentText}>
        <ContentText light small>
          <Trans
            i18nKey={`twin_communication.${activeQuestion.question}.${FeedbackType.GENE_RISK_INFO}.subheading_content`}
            components={[<span className={classes.emphasized} />]}
          />
        </ContentText>
      </Grid>

      <Grid item xs={12} className={classes.sources}>
        <Grid item container xs={12} direction="row" alignItems="center" className={classes.sourcesTitle}>
          <ChefHat className={classes.chefHat} />
          <Grid item>
            <ContentText bold>
              {t(`twin_communication.${activeQuestion.question}.${FeedbackType.GENE_RISK_INFO}.food_sources_header`)}
            </ContentText>
          </Grid>
        </Grid>
        <Grid container spacing={5} className={classes.sourceImageContainer}>
          {sources.map((source: GeneDataGroupSource, idx: number) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={12}
              lg={6}
              key={`${source.name.substring(0, 5)}-${idx}`}
              className={classes.sourceContainer}
            >
              <NutrientSourceBlock source={source} index={idx + 1} />
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};
